import React from 'react';
import { LuBookmark, LuBookmarkCheck, LuClipboard, LuFileText, LuImage, LuPencil, LuRotateCw, LuTable2, LuX } from 'react-icons/lu';
import { t } from '../../services/HelperService';
import {
  DOCUMENT_KIND_IMAGE, DOCUMENT_KIND_PASTE, DOCUMENT_KIND_TABLE, DOCUMENT_KIND_TEXT,
  DOCUMENT_STATUS_FAILED, DOCUMENT_STATUS_HELD, DOCUMENT_STATUS_PENDING, DOCUMENT_STATUS_PROCESSING, DOCUMENT_STATUS_READY, DOCUMENT_STATUS_REFUSED,
} from './assistantSessionApi';

const safeString = (v) => (v ?? '').toString();

/** The chip's own standing before the server has the file: the upload in progress. */
export const ATTACHMENT_STATUS_UPLOADING = 'uploading';

export const CHIP_HEIGHT = 28;

const kindIcon = (kind) => {
  switch (kind) {
    case DOCUMENT_KIND_IMAGE: return LuImage;
    case DOCUMENT_KIND_TABLE: return LuTable2;
    case DOCUMENT_KIND_PASTE: return LuClipboard;
    case DOCUMENT_KIND_TEXT: return LuFileText;
    default: return LuFileText;
  }
};

const formatBytes = (n) => {
  const bytes = Number(n) || 0;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return bytes > 0 ? `${bytes} B` : '';
};

/** The one-line standing a chip's tooltip and the strip's hint line say. */
export const describeAttachment = (a) => {
  if (!a) return '';
  switch (a.status) {
    case ATTACHMENT_STATUS_UPLOADING: return t('Uploading {{name}}…', { name: a.name });
    case DOCUMENT_STATUS_PENDING: return t('{{name}} is queued to be read.', { name: a.name });
    case DOCUMENT_STATUS_PROCESSING:
      return a.pageCount > 0
        ? t('Reading {{name}} — page {{done}} of {{count}}.', { name: a.name, done: a.pagesDone, count: a.pageCount })
        : t('Reading {{name}}…', { name: a.name });
    case DOCUMENT_STATUS_HELD: return `${a.name}: ${safeString(a.holdReason) || t('Large file — read it?')}`;
    case DOCUMENT_STATUS_FAILED: return `${a.name}: ${safeString(a.failure) || t('could not be read.')}`;
    case DOCUMENT_STATUS_REFUSED: return `${a.name}: ${safeString(a.failure) || t('refused — it looks like it holds a credential.')}`;
    case DOCUMENT_STATUS_READY:
    default: {
      const parts = [a.name];
      if (a.pageCount > 1) parts.push(t('{{count}} pages', { count: a.pageCount }));
      const size = formatBytes(a.bytes);
      if (size) parts.push(size);
      if (a.withMessage) parts.push(t('goes with your message'));
      if (a.keptKey) parts.push(t('kept in the library as "{{key}}"', { key: a.keptKey }));
      return parts.join(' · ');
    }
  }
};

const palette = (status, glow) => {
  if (glow) return { border: '#6f42c1', background: '#f3effa', color: '#3b2a6b' };
  switch (status) {
    case DOCUMENT_STATUS_HELD: return { border: '#fcd34d', background: '#fffbeb', color: '#92400e' };
    case DOCUMENT_STATUS_FAILED: return { border: '#fca5a5', background: '#fef2f2', color: '#991b1b' };
    case DOCUMENT_STATUS_REFUSED: return { border: '#d1d5db', background: '#f3f4f6', color: '#6b7280' };
    case ATTACHMENT_STATUS_UPLOADING:
    case DOCUMENT_STATUS_PENDING:
    case DOCUMENT_STATUS_PROCESSING: return { border: '#bfdbfe', background: '#eff6ff', color: '#1e3a8a' };
    default: return { border: '#dee2e6', background: '#fff', color: '#1e293b' };
  }
};

/**
 * One attached thing in the composer's strip: a 28px chip with a thumbnail (images)
 * or a kind icon, the name, and the standing — a thin progress bar while uploading /
 * reading, an amber "Read it?" when a large file is held, "Retry" when reading failed,
 * grey when refused. Hover (or focus) shows remove and "keep as reference"; a kept chip
 * carries the filled bookmark. A chip that goes out with the message (a pasted or
 * snipped screenshot) is marked with a blue dot; an image chip offers "Mark up" (a
 * pencil) when the host draws on images. Keyboard: Delete / Backspace removes, Enter opens.
 *
 * Props:
 * - attachment: { id, name, kind, status, progress?, pagesDone?, pageCount?, bytes?,
 *   holdReason?, failure?, thumb?, withMessage?, keptKey?, glow?, removable? }
 * - onRemove(attachment) / onKeep(attachment, buttonElement) / onConfirm(attachment) / onOpen(attachment) / onMarkup(attachment)
 * - disabled
 */
export const AttachmentChip = ({ attachment: a, disabled, onRemove, onKeep, onConfirm, onOpen, onMarkup }) => {
  const [hover, setHover] = React.useState(false);
  const [focus, setFocus] = React.useState(false);
  const keepRef = React.useRef(null);
  if (!a) return null;

  const Icon = kindIcon(a.kind);
  const colors = palette(a.status, a.glow);
  const busy = a.status === ATTACHMENT_STATUS_UPLOADING || a.status === DOCUMENT_STATUS_PENDING || a.status === DOCUMENT_STATUS_PROCESSING;
  const progress = a.status === ATTACHMENT_STATUS_UPLOADING
    ? Math.max(0, Math.min(1, Number(a.progress) || 0))
    : (a.status === DOCUMENT_STATUS_PROCESSING && a.pageCount > 0 ? Math.max(0.05, Math.min(1, a.pagesDone / a.pageCount)) : null);
  const showActions = (hover || focus) && !disabled;
  const canKeep = typeof onKeep === 'function' && a.status === DOCUMENT_STATUS_READY && !a.keptKey && !a.withMessage;
  const canRemove = typeof onRemove === 'function' && a.removable !== false;
  const canMarkup = typeof onMarkup === 'function' && a.kind === DOCUMENT_KIND_IMAGE && !!a.thumb && !busy;
  const title = describeAttachment(a);

  return (
    <div
      data-tagname="attachmentChip"
      data-status={a.status}
      role="group"
      tabIndex={disabled ? -1 : 0}
      title={title}
      aria-label={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocus(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setFocus(false); }}
      onKeyDown={(e) => {
        if (disabled) return;
        const key = e?.key;
        if ((key === 'Delete' || key === 'Backspace') && canRemove) { e.preventDefault(); e.stopPropagation(); onRemove(a); }
        else if (key === 'Enter' && typeof onOpen === 'function' && !busy) { e.preventDefault(); onOpen(a); }
      }}
      style={{
        position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6, height: CHIP_HEIGHT, maxWidth: 240,
        padding: a.thumb ? '0 6px 0 2px' : '0 6px 0 8px', borderRadius: 14, border: `1px solid ${colors.border}`, background: colors.background,
        color: colors.color, fontSize: 11, lineHeight: `${CHIP_HEIGHT - 2}px`, overflow: 'hidden', outline: 'none',
        boxShadow: a.glow ? '0 0 0 3px rgba(111, 66, 193, 0.18)' : (focus ? '0 0 0 2px rgba(13, 110, 253, 0.25)' : 'none'),
        transition: 'box-shadow 300ms ease, border-color 300ms ease, background 300ms ease',
        opacity: a.status === DOCUMENT_STATUS_REFUSED ? 0.75 : 1,
      }}
    >
      {a.thumb ? (
        <img
          src={a.thumb}
          alt=""
          onClick={() => !busy && onOpen?.(a)}
          style={{ width: 22, height: 22, objectFit: 'cover', borderRadius: 11, border: '1px solid rgba(0,0,0,0.08)', cursor: onOpen && !busy ? 'zoom-in' : 'default', flex: 'none' }}
        />
      ) : (
        <Icon size={13} style={{ flex: 'none', opacity: busy ? 0.7 : 1 }} />
      )}
      <span
        onClick={() => !busy && onOpen?.(a)}
        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: onOpen && !busy ? 'pointer' : 'default', maxWidth: 160 }}
      >
        {a.name}
      </span>
      {a.withMessage && (
        <span title={t('Goes with your message')} style={{ flex: 'none', width: 6, height: 6, borderRadius: 3, background: '#0d6efd', display: 'inline-block' }} />
      )}
      {a.keptKey && (
        <LuBookmarkCheck size={12} title={t('Kept in the library as "{{key}}"', { key: a.keptKey })} style={{ flex: 'none', color: '#6f42c1' }} />
      )}
      {a.status === DOCUMENT_STATUS_HELD && typeof onConfirm === 'function' && (
        <button
          type="button"
          data-tagname="attachmentReadIt"
          disabled={disabled}
          onClick={(e) => { e.stopPropagation(); onConfirm(a); }}
          style={{ flex: 'none', border: '1px solid #f59e0b', background: '#fff', color: '#92400e', borderRadius: 10, fontSize: 10, fontWeight: 600, lineHeight: '16px', padding: '0 7px', cursor: 'pointer' }}
        >
          {t('Read it')}
        </button>
      )}
      {a.status === DOCUMENT_STATUS_FAILED && typeof onConfirm === 'function' && (
        <button
          type="button"
          data-tagname="attachmentRetry"
          disabled={disabled}
          title={t('Try reading it again')}
          onClick={(e) => { e.stopPropagation(); onConfirm(a); }}
          style={{ flex: 'none', border: 'none', background: 'none', color: '#991b1b', padding: 0, lineHeight: 0, cursor: 'pointer' }}
        >
          <LuRotateCw size={12} />
        </button>
      )}
      {showActions && (canKeep || canRemove || canMarkup) && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flex: 'none', marginLeft: 2 }}>
          {canMarkup && (
            <button
              type="button"
              data-tagname="attachmentMarkup"
              title={t('Mark it up: draw a box, an arrow or a label on it')}
              onClick={(e) => { e.stopPropagation(); onMarkup(a); }}
              style={{ border: 'none', background: 'none', color: '#6f42c1', padding: 2, lineHeight: 0, cursor: 'pointer', borderRadius: 8 }}
            >
              <LuPencil size={12} />
            </button>
          )}
          {canKeep && (
            <button
              ref={keepRef}
              type="button"
              data-tagname="attachmentKeep"
              title={t('Keep as a design reference (every later session reads it from the library)')}
              onClick={(e) => { e.stopPropagation(); onKeep(a, keepRef.current); }}
              style={{ border: 'none', background: 'none', color: '#6f42c1', padding: 2, lineHeight: 0, cursor: 'pointer', borderRadius: 8 }}
            >
              <LuBookmark size={12} />
            </button>
          )}
          {canRemove && (
            <button
              type="button"
              data-tagname="attachmentRemove"
              title={t('Remove')}
              onClick={(e) => { e.stopPropagation(); onRemove(a); }}
              style={{ border: 'none', background: 'none', color: '#b71c1c', padding: 2, lineHeight: 0, cursor: 'pointer', borderRadius: 8 }}
            >
              <LuX size={12} />
            </button>
          )}
        </span>
      )}
      {busy && (
        <span
          aria-hidden="true"
          style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, background: 'rgba(13, 110, 253, 0.15)', overflow: 'hidden' }}
        >
          <span
            style={progress == null
              ? { position: 'absolute', top: 0, bottom: 0, width: '40%', background: '#0d6efd', animation: 'accentChipIndeterminate 1.2s ease-in-out infinite' }
              : { position: 'absolute', top: 0, bottom: 0, left: 0, width: `${Math.round(progress * 100)}%`, background: '#0d6efd', transition: 'width 200ms linear' }}
          />
        </span>
      )}
      <style>{'@keyframes accentChipIndeterminate { 0% { left: -40%; } 100% { left: 100%; } }'}</style>
    </div>
  );
};
